/**
 * Private Gallery API - Consent-Only Access Control
 * 
 * API wrapper for /api/private-gallery/* endpoints
 * Handles: request access, list pending requests, approve/deny, revoke, status check
 */

import { api } from './client';

/**
 * Request access to another user's private gallery
 * @param {string} ownerUserId - The profile owner's user ID
 * @param {string} message - Optional message with the request (max 800 chars)
 * @returns {Promise<{ok: boolean, status: string, message?: string}>}
 */
export async function requestPrivateGalleryAccess(ownerUserId, message) {
  const res = await api.post(`/private-gallery/request/${ownerUserId}`, { message });
  return res.data;
}

/**
 * List pending private gallery access requests (for gallery owner)
 * @returns {Promise<{ok: boolean, items: Array}>}
 */
export async function listPrivateGalleryRequests() {
  const res = await api.get(`/private-gallery/requests`);
  return res.data;
}

/**
 * Respond to a private gallery access request (approve/deny)
 * @param {string} viewerUserId - The user who requested access
 * @param {string} action - "approve" or "deny"
 * @returns {Promise<{ok: boolean, status: string}>}
 */
export async function respondPrivateGalleryRequest(viewerUserId, action) {
  const res = await api.post(`/private-gallery/respond/${viewerUserId}`, { action });
  return res.data;
}

/**
 * Revoke previously approved access to private gallery
 * @param {string} viewerUserId - The user whose access to revoke
 * @returns {Promise<{ok: boolean, status: string}>}
 */
export async function revokePrivateGalleryAccess(viewerUserId) {
  const res = await api.post(`/private-gallery/revoke/${viewerUserId}`);
  return res.data;
}

/**
 * Check current access status against a gallery owner
 * @param {string} ownerUserId - The profile owner's user ID
 * @returns {Promise<{ok: boolean, status: string}>} - status: "approved" | "pending" | "denied" | "none"
 */
export async function getPrivateGalleryStatus(ownerUserId) {
  const res = await api.get(`/private-gallery/status/${ownerUserId}`);
  return res.data;
}

/**
 * Owner proactively grants private gallery access (no request required)
 * @param {string} viewerUserId - The user to grant access to
 * @returns {Promise<{ok: boolean, status: string, viewer_user_id: string}>}
 */
export async function grantPrivateGalleryAccess(viewerUserId) {
  const res = await api.post(`/private-gallery/grant/${viewerUserId}`);
  return res.data;
}

export default {
  requestPrivateGalleryAccess,
  listPrivateGalleryRequests,
  respondPrivateGalleryRequest,
  revokePrivateGalleryAccess,
  getPrivateGalleryStatus,
  grantPrivateGalleryAccess,
};
